import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Diamond } from 'lucide-react';

/**
 * InAppNotificationBanner
 * Listens for SW_PUSH messages from the service worker and displays a
 * WhatsApp-style heads-up banner when a push notification arrives while
 * the user is already in the app (foreground).
 *
 * Works for ALL notification types — no per-notification code needed.
 * Any push with a `url` payload is automatically handled.
 */
export function InAppNotificationBanner() {
  const [notification, setNotification] = useState(null);
  const [visible, setVisible] = useState(false);
  const navigate = useNavigate();
  const timerRef = useRef(null);
  const progressRef = useRef(null);

  const dismiss = useCallback(() => {
    setVisible(false);
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  const handleOpen = useCallback(() => {
    if (notification?.url) navigate(notification.url);
    dismiss();
  }, [notification, navigate, dismiss]);

  useEffect(() => {
    if (!navigator.serviceWorker) return;

    const handler = (event) => {
      if (event.data?.type !== 'SW_PUSH') return;
      // Only show banner when the app is actually visible to the user
      if (document.visibilityState !== 'visible') return;

      // Cancel any running auto-dismiss from a previous notification
      if (timerRef.current) clearTimeout(timerRef.current);

      setNotification({
        title: event.data.title || 'DiaForge',
        body: event.data.body || '',
        url: event.data.url || '/',
      });
      setVisible(true);

      // Reset progress bar animation by briefly removing + re-adding the element
      if (progressRef.current) {
        progressRef.current.style.animation = 'none';
        progressRef.current.offsetHeight; // force reflow
        progressRef.current.style.animation = '';
      }

      timerRef.current = setTimeout(() => setVisible(false), 5000);
    };

    navigator.serviceWorker.addEventListener('message', handler);
    return () => {
      navigator.serviceWorker.removeEventListener('message', handler);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  // Unmount the DOM node after the slide-out animation finishes
  useEffect(() => {
    if (!visible && notification) {
      const t = setTimeout(() => setNotification(null), 350);
      return () => clearTimeout(t);
    }
  }, [visible, notification]);

  if (!notification) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        padding: '12px 12px 0',
        paddingTop: 'max(12px, env(safe-area-inset-top, 12px))',
        transform: visible ? 'translateY(0)' : 'translateY(-110%)',
        transition: 'transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)',
        pointerEvents: visible ? 'auto' : 'none',
      }}
    >
      <div
        onClick={handleOpen}
        style={{
          background: 'rgba(10, 18, 30, 0.97)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          borderRadius: '18px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,255,255,0.06)',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '12px 14px',
          cursor: 'pointer',
          maxWidth: '420px',
          margin: '0 auto',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {/* Brand icon */}
        <div style={{
          width: 42,
          height: 42,
          borderRadius: 12,
          background: 'linear-gradient(135deg, #075E54 0%, #128C7E 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}>
          <Diamond size={18} color="#fff" />
        </div>

        {/* Text */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{
            color: '#fff',
            fontSize: 13,
            fontWeight: 700,
            margin: 0,
            lineHeight: 1.3,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {notification.title}
          </p>
          <p style={{
            color: 'rgba(255,255,255,0.65)',
            fontSize: 12,
            margin: '2px 0 0',
            lineHeight: 1.4,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}>
            {notification.body}
          </p>
        </div>

        {/* Open chip */}
        <div style={{
          flexShrink: 0,
          background: 'rgba(18, 140, 126, 0.2)',
          border: '1px solid rgba(18, 140, 126, 0.5)',
          borderRadius: 20,
          padding: '4px 10px',
          fontSize: 11,
          fontWeight: 600,
          color: '#4ECDC4',
          whiteSpace: 'nowrap',
        }}>
          Open
        </div>

        {/* Dismiss button */}
        <button
          onClick={(e) => { e.stopPropagation(); dismiss(); }}
          style={{
            flexShrink: 0,
            width: 24,
            height: 24,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.08)',
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            padding: 0,
            color: 'rgba(255,255,255,0.6)',
          }}
          data-testid="notif-banner-dismiss"
        >
          <X size={13} />
        </button>

        {/* Auto-dismiss progress bar */}
        <div style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          height: 2,
          background: 'rgba(18, 140, 126, 0.6)',
          borderRadius: '0 0 18px 18px',
          animation: 'notif-drain 5s linear forwards',
        }} ref={progressRef} />
      </div>

      <style>{`
        @keyframes notif-drain {
          from { width: 100%; }
          to   { width: 0%; }
        }
      `}</style>
    </div>
  );
}
